import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { getDay } from "date-fns";
import momentTz from "moment-timezone";
import "react-datepicker/dist/react-datepicker.css";
import { ConversationStep } from "../../interfaces";
import { hexToRgb, trackEvent } from "../../util";
import moment from "moment";
import "./AppointmentCalendar.scss";
import axios from "axios";

interface IProps {
  step?: ConversationStep;
  values?: any;
  triggerNextStep?: (data?: {
    value?: string | number;
    currentStep: ConversationStep;
    trigger: string;
  }) => void;
  setDataValues?: (values: any) => void;
}

const { REACT_APP_MORTGAGE_URL } = process.env;

const AppointmentCalendar: React.FC<IProps> = ({
  step,
  values,
  triggerNextStep,
  setDataValues,
}) => {
  const datePickerRef = useRef(null);

  const [inputDate, setInputDate] = useState(new Date());
  const [canTalkNow, setCanTalkNow] = useState(false);
  const [events, setEvents] = useState([]);

  const theme = step?.theme!;

  const convertDefaultInputDate = (date: Date) => {
    const mo1 = momentTz(date);
    const mo2 = momentTz(date).tz("Europe/London");
    const modifiedDate = moment(date);
    modifiedDate.subtract(mo1.utcOffset() - mo2.utcOffset(), "minutes");

    return modifiedDate.toDate();
  };

  const getEvents = useCallback(
    async (date: Date) => {
      const formattedDate = `${date.getFullYear()}-${(
        "0" +
        (date.getMonth() + 1)
      ).slice(-2)}-${("0" + date.getDate()).slice(-2)}`;

      if (
        theme?.apiKey &&
        (theme?.emailCustomization?.graphCredentials?.email ||
          theme?.emailCustomization?.googleCredentials?.email)
      ) {
        try {
          const {
            data: { events },
          } = await axios.post(`${REACT_APP_MORTGAGE_URL}/api/teams/events`, {
            apiKey: theme?.apiKey,
            startTime: formattedDate,
            endTime: formattedDate,
          });
          setEvents(events);
        } catch (err) {
          console.error(err);
        }
      }
    },
    [theme]
  );

  const isTimeDisabled = (time: string) => {
    const fullDate = `${inputDate.getFullYear()}-${(
      "0" +
      (inputDate.getMonth() + 1)
    ).slice(-2)}-${("0" + inputDate.getDate()).slice(-2)}`;

    const startDate = new Date(`${fullDate}T${time}:00`);
    const endDate = new Date(`${fullDate}T${time}:00`);
    endDate.setMinutes(
      endDate.getMinutes() + 60 * (theme?.workHours?.timeInterval || 0.5)
    );
    const utcDate = startDate.toISOString();
    const utcEndDate = endDate.toISOString();
    const utcTime = utcDate.split("T")[1].split(":");
    const utcEndTime = utcEndDate.split("T")[1].split(":");
    const formattedDate = `${utcDate.split("T")[0]}T${utcTime[0]}:${
      utcTime[1]
    }`;
    const formattedEndDate = `${utcEndDate.split("T")[0]}T${utcEndTime[0]}:${
      utcEndTime[1]
    }`;
    const filteredEvents = events.find(
      (event: any) =>
        new Date(formattedEndDate) > new Date(event.startTime) &&
        new Date(formattedDate) < new Date(event.endTime)
    );
    return isCurrentDateTimeDisabled(time) || filteredEvents !== undefined;
  };

  const isCurrentDateTimeDisabled = (timeToCheck: string) => {
    const timezoneDateTimeString = new Date()
      .toLocaleString("en-GB", { timeZone: "Europe/London", hour12: false })
      .split(",");
    const timezoneDate = timezoneDateTimeString[0].trim();
    const timezoneTime = timezoneDateTimeString[1].trim();
    const month = parseInt(timezoneDate.split("/")[0]);
    const localDate = parseInt(timezoneDate.split("/")[1]);
    const hours = timeToCheck.split(":")[0];

    //today
    if (
      localDate === inputDate?.getDate() &&
      month === inputDate?.getMonth() + 1
    ) {
      const minutes = timeToCheck.split(":")[1];
      const currentHours = timezoneTime.split(":")[0];
      const currentMinutes = timezoneTime.split(":")[1];

      if (hours === currentHours) return minutes <= currentMinutes;
      return hours <= currentHours;
    }

    return false;
  };

  const isWeekDay = (date: Date) => {
    const day = getDay(date);
    return day !== 0 && day !== 6;
  };

  const getIncludedTimes = () => {
    const today = new Date();
    const startHours = `${
      "0" + theme?.workHours?.startTime?.split(":")[0]
    }`.slice(-2);
    const startMinutes = `${
      "0" + theme?.workHours?.startTime?.split(":")[1]
    }`.slice(-2);
    const endHours = `${"0" + theme?.workHours?.endTime?.split(":")[0]}`.slice(
      -2
    );
    const endMinutes = `${
      "0" + theme?.workHours?.endTime?.split(":")[1]
    }`.slice(-2);
    const timeInterval = 60 * (theme?.workHours?.timeInterval || 0.5);

    const endFullDate = new Date(
      `${today.getFullYear()}-${("0" + (today.getMonth() + 1)).slice(-2)}-${(
        "0" + today.getDate()
      ).slice(-2)}T${endHours}:${endMinutes}:00`
    );
    let times = [];

    let startFullDate = new Date(
      `${today.getFullYear()}-${("0" + (today.getMonth() + 1)).slice(-2)}-${(
        "0" + today.getDate()
      ).slice(-2)}T${startHours}:${startMinutes}:00`
    );

    while (startFullDate.getTime() <= endFullDate.getTime()) {
      times.push(
        new Date(
          `${startFullDate.getFullYear()}-${(
            "0" +
            (startFullDate.getMonth() + 1)
          ).slice(-2)}-${("0" + startFullDate.getDate()).slice(-2)}T${(
            "0" + startFullDate.getHours()
          ).slice(-2)}:${("0" + startFullDate.getMinutes()).slice(-2)}:00`
        )
      );

      startFullDate.setMinutes(startFullDate.getMinutes() + timeInterval);
    }

    return times;
  };

  const isWeekHours = (time: Date) => {
    const currentTimezoneDateTimeString = new Date()
      .toLocaleString("en-GB", { timeZone: "Europe/London", hour12: false })
      .split(",");
    const convertedTimezone = new Date(time)
      .toLocaleString("en-GB", { timeZone: "Europe/London", hour12: false })
      .split(",");

    const convertedHour = parseInt(convertedTimezone[1].split(":")[0]);
    const convertedMinutes = parseInt(convertedTimezone[1].split(":")[1]);

    if (convertedTimezone[0] === currentTimezoneDateTimeString[0]) {
      const currentHour = parseInt(
        currentTimezoneDateTimeString[1].split(":")[0]
      );

      return (
        // selectedDate.getHours() >= 9 &&
        // selectedDate.getHours() <= 17 &&
        // selectedDate.getMinutes() !== 30 &&
        currentHour < convertedHour &&
        !isTimeDisabled(
          `${("0" + convertedHour).slice(-2)}:${("0" + convertedMinutes).slice(
            -2
          )}`
        )
      );
    }

    return (
      // selectedDate.getHours() >= 9 &&
      // selectedDate.getHours() <= 17 &&
      // selectedDate.getMinutes() !== 30 &&
      !isTimeDisabled(
        `${("0" + convertedHour).slice(-2)}:${("0" + convertedMinutes).slice(
          -2
        )}`
      )
    );
  };

  const confirmDate = (e: any) => {
    e.preventDefault();
    if (inputDate.getHours() > 0) {
      const formattedDate = `${inputDate.getFullYear()}-${(
        `0` +
        (inputDate.getMonth() + 1)
      ).slice(-2)}-${(`0` + inputDate.getDate()).slice(-2)}T${(
        `0` + inputDate.getHours()
      ).slice(-2)}:${("0" + inputDate.getMinutes()).slice(-2)}:00Z`;

      setDataValues!({
        ...values,
        contact_requested: "",
        appointment_date: formattedDate,
      });

      setTimeout(() => {
        triggerNextStep!({
          value: formattedDate,
          currentStep: step!,
          trigger: step!.trigger,
        });
      }, 1000);
    }
  };

  const talkNow = (e: any) => {
    e.preventDefault();
    const date: Date = new Date();

    if (date.getHours() > 0) {
      const formattedDate = `${date.getFullYear()}-${(
        `0` +
        (date.getMonth() + 1)
      ).slice(-2)}-${(`0` + date.getDate()).slice(-2)}T${(
        `0` + date.getHours()
      ).slice(-2)}:00:00Z`;

      setDataValues!({
        ...values,
        contact_requested: "Talk Now",
        appointment_date: formattedDate,
      });

      setTimeout(() => {
        triggerNextStep!({
          currentStep: step!,
          trigger: step!.trigger,
        });
      }, 1000);
    }
  };

  const applyPrimaryColor = (
    node: HTMLElement,
    property: string,
    color: string
  ) => {
    if (node) {
      node.style.setProperty(property, color, "important");
    }
  };

  const updateStyling = () => {
    document
      .querySelectorAll<HTMLElement>(
        ".react-datepicker__day:not(.react-datepicker__day--disabled):not(.react-datepicker__day--selected)"
      )
      .forEach((element) => {
        element.style.backgroundColor = "#FFF";
      });

    document
      .querySelectorAll<HTMLElement>(
        ".react-datepicker__time-list-item:not(.react-datepicker__time-list-item--disabled):not(.react-datepicker__time-list-item--selected)"
      )
      .forEach((element) => {
        element.style.backgroundColor = "#FFF";
      });

    const selectedDayNodes = document.querySelectorAll<HTMLElement>(
      ".react-datepicker__day--selected"
    );
    applyPrimaryColor(
      selectedDayNodes[selectedDayNodes.length - 1],
      "background-color",
      theme?.customization?.primaryColor!
    );

    const keyboardSelectedNodes = document.querySelectorAll<HTMLElement>(
      ".react-datepicker__day--keyboard-selected"
    );
    applyPrimaryColor(
      keyboardSelectedNodes[keyboardSelectedNodes.length - 1],
      "background-color",
      theme?.customization?.primaryColor!
    );

    const currentMonthNodes = document.querySelectorAll<HTMLElement>(
      ".react-datepicker__current-month"
    );
    applyPrimaryColor(
      currentMonthNodes[currentMonthNodes.length - 1],
      "color",
      theme?.customization?.primaryColor!
    );

    const timeSelectedNodes = document.querySelectorAll<HTMLElement>(
      ".react-datepicker__time-list-item--selected"
    );
    applyPrimaryColor(
      timeSelectedNodes[timeSelectedNodes.length - 1],
      "background-color",
      theme?.customization?.primaryColor!
    );

    const timeHeaderNodes = document.querySelectorAll<HTMLElement>(
      ".react-datepicker-time__header"
    );
    applyPrimaryColor(
      timeHeaderNodes[timeHeaderNodes.length - 1],
      "color",
      theme?.customization?.primaryColor!
    );
  };

  const checkWorkHours = () => {
    const start = moment(theme.workHours?.startTime || "09:00:00", "HH:mm:ss");
    const end = moment(theme.workHours?.endTime || "16:00:00", "HH:mm:ss");
    const isWorkHours = moment().isBetween(start, end);
    const dayToday = moment().format("dddd").toLowerCase();
    const isWorkDay = theme.workHours?.days[dayToday];
    if (isWorkHours && isWorkDay) {
      setCanTalkNow(true);
      return;
    }
    setCanTalkNow(false);
  };

  useEffect(() => {
    setTimeout(() => {
      updateStyling();
      checkWorkHours();
    }, 500);
    getEvents(inputDate);
    /*eslint-disable react-hooks/exhaustive-deps*/
  }, []);

  return (
    <div className="conversation-message-bubble react-datepicker-wrapper">
      <div className="react-datepicker-buttons">
        {theme?.customization?.expertPhoneNumber && (
          //TODO:
          //1. check workHours props, see if the browser time is in between work hours and days
          <button
            id={`${step?.id!}-talk-now`}
            type="button"
            className="react-date-picker-talk-button"
            style={{
              backgroundColor: theme?.customization?.secondaryColor,
              borderColor: theme?.customization?.secondaryColor,
              boxShadow: `0 1px 12px 0 rgb(${
                hexToRgb(theme?.customization?.secondaryColor!)?.r
              } ${hexToRgb(theme?.customization?.secondaryColor!)?.g} ${
                hexToRgb(theme?.customization?.secondaryColor!)?.b
              } / 22%)`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#FFF";
              e.currentTarget.style.border = `2px solid ${theme.customization
                ?.secondaryColor!}`;
              e.currentTarget.style.color =
                theme.customization?.secondaryColor!;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                theme.customization?.secondaryColor!;
              e.currentTarget.style.border = `2px solid ${theme.customization
                ?.secondaryColor!}`;
              e.currentTarget.style.color = "#FFF";
            }}
            onClick={talkNow}
            disabled={!canTalkNow}
          >
            Let's Talk Now
          </button>
        )}
        {(isWeekHours(inputDate) &&
          (inputDate.getMinutes() === 0 || inputDate.getMinutes() === 30)) ||
        theme?.appointmentCustomization?.disableAppointmentTime ? (
          <button
            type="button"
            id={`${step?.id!}-confirm-date`}
            className="react-date-picker-confirm-button"
            style={{
              backgroundColor: theme?.customization?.primaryColor,
              borderColor: theme?.customization?.primaryColor,
              boxShadow: `0 1px 12px 0 rgb(${
                hexToRgb(theme?.customization?.primaryColor!)?.r
              } ${hexToRgb(theme?.customization?.primaryColor!)?.g} ${
                hexToRgb(theme?.customization?.primaryColor!)?.b
              } / 22%)`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#FFF";
              e.currentTarget.style.border = `2px solid ${theme.customization
                ?.primaryColor!}`;
              e.currentTarget.style.color = theme.customization?.primaryColor!;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                theme.customization?.primaryColor!;
              e.currentTarget.style.border = `2px solid ${theme.customization
                ?.primaryColor!}`;
              e.currentTarget.style.color = "#FFF";
            }}
            onClick={confirmDate}
          >
            Confirm Date
          </button>
        ) : (
          <button
            type="button"
            id={`${step?.id!}-confirm-date`}
            className="react-date-picker-confirm-button"
            style={{
              backgroundColor: theme?.customization?.primaryColor,
              borderColor: theme?.customization?.primaryColor,
              boxShadow: `0 1px 12px 0 rgb(${
                hexToRgb(theme?.customization?.primaryColor!)?.r
              } ${hexToRgb(theme?.customization?.primaryColor!)?.g} ${
                hexToRgb(theme?.customization?.primaryColor!)?.b
              } / 22%)`,
              opacity: 0.5,
              pointerEvents: "none",
            }}
          >
            Confirm Date
          </button>
        )}
      </div>
      {/* parseInt(theme?.workHours?.startTime!) || 9 */}
      <DatePicker
        ref={datePickerRef}
        selected={inputDate}
        open={true}
        minDate={new Date()}
        inline
        filterDate={isWeekDay}
        filterTime={isWeekHours}
        includeTimes={getIncludedTimes()}
        timeIntervals={60 * (theme?.workHours?.timeInterval || 0.5)}
        onMonthChange={() => {
          setTimeout(() => {
            updateStyling();
          }, 500);
        }}
        onYearChange={() => {
          setTimeout(() => {
            updateStyling();
          }, 500);
        }}
        onChange={(changedDate) => {
          const changedFullDate = moment(changedDate).format("YYYYMMDD");
          const currentFullDate = moment(inputDate).format("YYYYMMDD");

          if (changedFullDate !== currentFullDate) {
            getEvents(changedDate!);
          }

          setInputDate(changedDate!);
          setTimeout(() => {
            updateStyling();
          }, 500);
        }}
        showTimeSelect={
          !theme?.appointmentCustomization?.disableAppointmentTime
        }
        timeFormat="HH:mm aa"
        dateFormat="MMMM d, yyyy h:mm aa"
        calendarStartDay={1}
      />
    </div>
  );
};

export default React.memo(AppointmentCalendar);
